import { Dispatch, SetStateAction } from "react";
import { Icon } from "semantic-ui-react";

import { CarriersFilter } from "hooks/useCarriersFilter";
import CarrierFilterLabelPopup from "./CarrierFilterLabelPopup";
import {
  defaultAccountOwners,
  statusToColorMap,
} from "../CarrierFilterForm/CarrierFilterForm";
import { useUsername } from "hooks/useUsername";
import { trailerBuildTypeOptions } from "components/FleetCapabilities/FleetCapabilities.form";

import styles from "./CarrierFilterBar.module.scss";
import { dateTimeLocale } from "shared/utils/dateTimeFormatting";

type CarrierFilterLabelsProps = {
  carriersFilter: CarriersFilter;
  setCarriersFilter: Dispatch<SetStateAction<CarriersFilter>>;
  resetQuickCapacity: () => void;
};

export const convertToPopupDateString = (dateString: string): string => {
  const parsedDate = new Date(dateString);

  if (isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(dateTimeLocale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  }).format(parsedDate);
};

const CarrierFilterLabels = (props: CarrierFilterLabelsProps) => {
  const username = useUsername();

  const resetField = (partialFilter: Partial<CarriersFilter>) => {
    props.resetQuickCapacity();
    props.setCarriersFilter({
      ...props.carriersFilter,
      ...partialFilter,
    });
  };

  return (
    <div className={styles.labelsContainer}>
      <div className={styles.filterTitle}>Filters:</div>

      {props.carriersFilter.search ? (
        <CarrierFilterLabelPopup
          labelText={`Search: ${props.carriersFilter.search.trim()}`}
          title="Remove Search"
          resetField={() => resetField({ search: "" })}
        >
          {props.carriersFilter.search.trim()}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.myFavoriteCarriers ? (
        <CarrierFilterLabelPopup
          labelText="My Favorite Carriers"
          title="Remove My Favorite Carriers"
          resetField={() => resetField({ myFavoriteCarriers: false })}
        >
          My Favorite Carriers
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.statuses.length ? (
        <CarrierFilterLabelPopup
          labelText="Status"
          title="Remove Statuses"
          resetField={() => resetField({ statuses: [] })}
        >
          {props.carriersFilter.statuses.map((status) => (
            <div key={status}>
              <Icon name="circle" color={statusToColorMap[status]} /> {status}
            </div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.myCarriers ||
      props.carriersFilter.unownedCarriers ||
      props.carriersFilter.accountOwners.length ? (
        <CarrierFilterLabelPopup
          labelText="Account Owner"
          title="Remove Account Owners"
          resetField={() =>
            resetField({
              myCarriers: false,
              unownedCarriers: false,
              accountOwners: [],
            })
          }
        >
          {props.carriersFilter.myCarriers && (
            <div>
              <Icon name="user circle" />{" "}
              {username
                ? `${defaultAccountOwners.user} (${username})`
                : defaultAccountOwners.user}
            </div>
          )}
          {props.carriersFilter.unownedCarriers && (
            <div>
              <Icon name="ban" /> {defaultAccountOwners.none}
            </div>
          )}
          {props.carriersFilter.accountOwners.map((accountOwner) => (
            <div key={accountOwner.key}>{accountOwner.text}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.capacityPickupDate ? (
        <CarrierFilterLabelPopup
          labelText="Capacity Pickup Date"
          title="Remove Capacity Pickup Date"
          resetField={() => resetField({ capacityPickupDate: "" })}
        >
          {convertToPopupDateString(props.carriersFilter.capacityPickupDate)}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.capacityOrigin ? (
        <CarrierFilterLabelPopup
          labelText="Capacity Origin"
          title="Remove Capacity Origin"
          resetField={() => resetField({ capacityOrigin: "" })}
        >
          {props.carriersFilter.capacityOrigin}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.capacityDestination ? (
        <CarrierFilterLabelPopup
          labelText="Capacity Destination"
          title="Remove Capacity Destination"
          resetField={() => resetField({ capacityDestination: "" })}
        >
          {props.carriersFilter.capacityDestination}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.hasEvTrucks ? (
        <CarrierFilterLabelPopup
          labelText="EV Trucks"
          title="Remove EV Trucks filter"
          resetField={() => resetField({ hasEvTrucks: false })}
        >
          EV Trucks: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.hasHvoTrucks ? (
        <CarrierFilterLabelPopup
          labelText="HVO Trucks"
          title="Remove HVO Trucks filter"
          resetField={() => resetField({ hasHvoTrucks: false })}
        >
          HVO Trucks: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.hasGpsTrackTrace ? (
        <CarrierFilterLabelPopup
          labelText="GPS / Track & Trace"
          title="Remove GPS filter"
          resetField={() => resetField({ hasGpsTrackTrace: false })}
        >
          GPS / Track & Trace: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.notActiveMoreThanSixMonths ? (
        <CarrierFilterLabelPopup
          labelText="Not Active > 6 Months"
          title="Remove Not Active More Than 6 Months filter"
          resetField={() => resetField({ notActiveMoreThanSixMonths: false })}
        >
          No shipment delivered in the last 6 months
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shippeoOnboardedStatus ? (
        <CarrierFilterLabelPopup
          labelText="Shippeo Onboarded"
          title="Remove Shippeo filter"
          resetField={() => resetField({ shippeoOnboardedStatus: "" })}
        >
          Shippeo Onboarded:{" "}
          {props.carriersFilter.shippeoOnboardedStatus === "true"
            ? "Yes"
            : "No"}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.lf4ForMercedes ? (
        <CarrierFilterLabelPopup
          labelText="LF4 for Mercedes"
          title="Remove LF4 filter"
          resetField={() => resetField({ lf4ForMercedes: false })}
        >
          LF4 for Mercedes: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.vdiCertification ? (
        <CarrierFilterLabelPopup
          labelText="VDI Certification"
          title="Remove VDI filter"
          resetField={() => resetField({ vdiCertification: false })}
        >
          VDI Certification: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.daimlerRulesCompliance ? (
        <CarrierFilterLabelPopup
          labelText="Daimler Rules"
          title="Remove Daimler Rules filter"
          resetField={() => resetField({ daimlerRulesCompliance: false })}
        >
          Daimler Rules Compliance: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.toyotaRulesCompliance ? (
        <CarrierFilterLabelPopup
          labelText="Toyota Rules"
          title="Remove Toyota Rules filter"
          resetField={() => resetField({ toyotaRulesCompliance: false })}
        >
          Toyota Rules Compliance: Yes
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.trailerBuildTypeIds.length ? (
        <CarrierFilterLabelPopup
          labelText="Trailer Build Type"
          title="Remove Trailer Build Types"
          resetField={() => resetField({ trailerBuildTypeIds: [] })}
        >
          {props.carriersFilter.trailerBuildTypeIds.map((id) => {
            const opt = trailerBuildTypeOptions.find((o) => o.id === id);
            return <div key={id}>{opt?.name ?? id}</div>;
          })}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.preferredLaneOriginCountryCodes.length ? (
        <CarrierFilterLabelPopup
          labelText="Preferred Origin"
          title="Remove Preferred Origin filter"
          resetField={() =>
            resetField({ preferredLaneOriginCountryCodes: [] })
          }
        >
          {props.carriersFilter.preferredLaneOriginCountryCodes.map((code) => (
            <div key={code}>{code}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.preferredLaneDestinationCountryCodes.length ? (
        <CarrierFilterLabelPopup
          labelText="Preferred Destination"
          title="Remove Preferred Destination filter"
          resetField={() =>
            resetField({ preferredLaneDestinationCountryCodes: [] })
          }
        >
          {props.carriersFilter.preferredLaneDestinationCountryCodes.map(
            (code) => (
              <div key={code}>{code}</div>
            )
          )}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.carrierCountries.length ? (
        <CarrierFilterLabelPopup
          labelText="Country"
          title="Remove Country filter"
          resetField={() => resetField({ carrierCountries: [] })}
        >
          {props.carriersFilter.carrierCountries.map((country) => (
            <div key={country}>{country}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.minInsuranceAmount ? (
        <CarrierFilterLabelPopup
          labelText={`Insurance >= ${props.carriersFilter.minInsuranceAmount}`}
          title="Remove Insurance filter"
          resetField={() => resetField({ minInsuranceAmount: "" })}
        >
          Min Insurance Amount: {props.carriersFilter.minInsuranceAmount}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.lastDeliveredFrom ? (
        <CarrierFilterLabelPopup
          labelText="Last Delivered From"
          title="Remove Last Delivered From filter"
          resetField={() => resetField({ lastDeliveredFrom: "" })}
        >
          Last Delivered From:{" "}
          {convertToPopupDateString(props.carriersFilter.lastDeliveredFrom)}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.lastDeliveredTo ? (
        <CarrierFilterLabelPopup
          labelText="Last Delivered To"
          title="Remove Last Delivered To filter"
          resetField={() => resetField({ lastDeliveredTo: "" })}
        >
          Last Delivered To:{" "}
          {convertToPopupDateString(props.carriersFilter.lastDeliveredTo)}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentOriginCountryCodes.length ? (
        <CarrierFilterLabelPopup
          labelText="Shipment Origin Country"
          title="Remove Shipment Origin Country filter"
          resetField={() => resetField({ shipmentOriginCountryCodes: [] })}
        >
          {props.carriersFilter.shipmentOriginCountryCodes.map((code) => (
            <div key={code}>{code}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentOriginCities.length ? (
        <CarrierFilterLabelPopup
          labelText="Shipment Origin City"
          title="Remove Shipment Origin City filter"
          resetField={() => resetField({ shipmentOriginCities: [] })}
        >
          {props.carriersFilter.shipmentOriginCities.map((city) => (
            <div key={city}>{city}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentDestinationCountryCodes.length ? (
        <CarrierFilterLabelPopup
          labelText="Shipment Destination Country"
          title="Remove Shipment Destination Country filter"
          resetField={() =>
            resetField({ shipmentDestinationCountryCodes: [] })
          }
        >
          {props.carriersFilter.shipmentDestinationCountryCodes.map((code) => (
            <div key={code}>{code}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentDestinationCities.length ? (
        <CarrierFilterLabelPopup
          labelText="Shipment Destination City"
          title="Remove Shipment Destination City filter"
          resetField={() => resetField({ shipmentDestinationCities: [] })}
        >
          {props.carriersFilter.shipmentDestinationCities.map((city) => (
            <div key={city}>{city}</div>
          ))}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentRouteCompletedDateFrom ? (
        <CarrierFilterLabelPopup
          labelText="Route Complete Date From"
          title="Remove Route Complete Date From filter"
          resetField={() => resetField({ shipmentRouteCompletedDateFrom: "" })}
        >
          Route Complete Date From:{" "}
          {convertToPopupDateString(
            props.carriersFilter.shipmentRouteCompletedDateFrom
          )}
        </CarrierFilterLabelPopup>
      ) : null}

      {props.carriersFilter.shipmentRouteCompletedDateTo ? (
        <CarrierFilterLabelPopup
          labelText="Route Complete Date To"
          title="Remove Route Complete Date To filter"
          resetField={() => resetField({ shipmentRouteCompletedDateTo: "" })}
        >
          Route Complete Date To:{" "}
          {convertToPopupDateString(
            props.carriersFilter.shipmentRouteCompletedDateTo
          )}
        </CarrierFilterLabelPopup>
      ) : null}
    </div>
  );
};

export default CarrierFilterLabels;
